import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { ReviewAnalysisApiService } from 'src/services/reviewAnalysisApi'
import { ReviewComparisonCreatePayload } from 'src/services/reviewAnalysisApi/interfaces/reviewComparison.interface'

/*
 * Review Analysis → Reporting & Comparison.
 *
 * There is nothing to poll: the backend computes a report on every read, so a
 * comparison is either saved or it isn't. What these hooks do beyond fetching
 * is keep the list honest after a create or a delete.
 */

export const useGetReviewComparisonsQuery = (campaignId: string) =>
  useQuery({
    enabled: !!campaignId,
    queryFn: () => ReviewAnalysisApiService.getComparisons(campaignId),
    queryKey: [QueryKey.GET_REVIEW_ANALYSIS_COMPARISONS, campaignId],
  })

// The facilities the picker can offer. Separate from the campaign's own
// facility list because this one is "what has reviews", which is the only
// thing a comparison can be made of.
export const useGetReviewComparisonFacilitiesQuery = (campaignId: string) =>
  useQuery({
    enabled: !!campaignId,
    queryFn: () => ReviewAnalysisApiService.getComparisonFacilities(campaignId),
    queryKey: [QueryKey.GET_REVIEW_ANALYSIS_COMPARISON_FACILITIES, campaignId],
  })

// One comparison's report. Computed on read, so this is the request that does
// the work — and why it is only fired once a row has actually been opened.
export const useGetReviewComparisonQuery = (
  campaignId: string,
  comparisonId?: string
) =>
  useQuery({
    enabled: !!campaignId && !!comparisonId,
    queryFn: () =>
      ReviewAnalysisApiService.getComparison(
        campaignId,
        comparisonId as string
      ),
    queryKey: [
      QueryKey.GET_REVIEW_ANALYSIS_COMPARISON,
      campaignId,
      comparisonId,
    ],
  })

export const useReviewComparisonMutations = (campaignId: string) => {
  const queryClient = useQueryClient()

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_REVIEW_ANALYSIS_COMPARISONS, campaignId],
    })

  const createComparison = useMutation({
    mutationFn: (payload: ReviewComparisonCreatePayload) =>
      ReviewAnalysisApiService.createComparison(campaignId, payload),
    onSuccess: invalidateList,
  })

  const deleteComparison = useMutation({
    mutationFn: (comparisonId: string) =>
      ReviewAnalysisApiService.deleteComparison(campaignId, comparisonId),
    onSuccess: invalidateList,
  })

  return { createComparison, deleteComparison }
}
